import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Paper } from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import { Brain, Zap, BookOpen, GitBranch } from "lucide-react";

// Animaciones más simples y confiables
const waveAnimation = keyframes`
  0% { 
    transform: scale(0.8); 
    opacity: 0.3; 
  }
  50% { 
    transform: scale(1.1); 
    opacity: 1; 
  }
  100% { 
    transform: scale(1); 
    opacity: 0.8; 
  }
`;

const pulseAnimation = keyframes`
  0%, 100% { 
    opacity: 0.6; 
  }
  50% { 
    opacity: 1; 
  }
`;

const flowAnimation = keyframes`
  0% { 
    transform: translateX(-100%); 
    opacity: 0; 
  }
  50% { 
    opacity: 1; 
  }
  100% { 
    transform: translateX(100%); 
    opacity: 0; 
  }
`;

// Componente de matriz simple pero efectivo
const AnalysisMatrix = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, 1fr)",
  gridTemplateRows: "repeat(8, 1fr)",
  gap: "3px",
  width: "300px",
  height: "200px",
  margin: "0 auto",
  padding: "16px",
  borderRadius: "12px",
  backgroundColor: "rgba(0, 0, 0, 0.2)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
}));

const MatrixCell = styled(Box)(({ phase, delay, isActive }) => ({
  borderRadius: "4px",
  height: "16px",
  width: "100%",
  minHeight: "16px",
  transition: "all 0.3s ease",
  opacity: isActive ? 1 : 0.3,
  animation: isActive
    ? `${waveAnimation} 1s ease-in-out ${delay}ms infinite`
    : "none",
  backgroundColor:
    phase === "tipo"
      ? "#FF9800"
      : phase === "nombre"
      ? "#2196F3"
      : phase === "nociones"
      ? "#4CAF50"
      : phase === "impactos"
      ? "#9C27B0"
      : "rgba(255, 255, 255, 0.2)",
  boxShadow: isActive
    ? `0 0 6px ${
        phase === "tipo"
          ? "#FF9800"
          : phase === "nombre"
          ? "#2196F3"
          : phase === "nociones"
          ? "#4CAF50"
          : phase === "impactos"
          ? "#9C27B0"
          : "rgba(255, 255, 255, 0.3)"
      }`
    : "none",
}));

const PhaseIndicator = styled(Box)(({ isActive, color }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "12px 16px",
  borderRadius: "8px",
  backgroundColor: isActive ? `${color}20` : "rgba(255, 255, 255, 0.05)",
  border: `1px solid ${isActive ? color : "rgba(255, 255, 255, 0.1)"}`,
  transition: "all 0.3s ease",
  transform: isActive ? "scale(1.02)" : "scale(1)",
  "& .phase-icon": {
    color: isActive ? color : "rgba(255, 255, 255, 0.5)",
    animation: isActive ? `${pulseAnimation} 2s infinite` : "none",
  },
  "& .phase-text": {
    color: isActive ? "white" : "rgba(255, 255, 255, 0.7)",
    fontWeight: isActive ? "bold" : "normal",
  },
}));

const FlowLine = styled(Box)(({ isActive, color }) => ({
  height: "3px",
  backgroundColor: color,
  borderRadius: "2px",
  position: "relative",
  overflow: "hidden",
  opacity: isActive ? 1 : 0.3,
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
    animation: isActive ? `${flowAnimation} 2s infinite` : "none",
  },
}));

const AnalyticalLoadingScreen = ({ processingStep, isProcessing }) => {
  const [currentPhase, setCurrentPhase] = useState("tipo"); // Empezar con tipo
  const [matrixData, setMatrixData] = useState([]);

  // Configuración de fases memoizada (orden correcto del análisis)
  const phases = useMemo(
    () => [
      {
        key: "tipo",
        label: "Comparando Tipo",
        color: "#FF9800",
        icon: BookOpen,
        description: "Analizando tipos de símbolos...",
      },
      {
        key: "nombre",
        label: "Comparando Nombre",
        color: "#2196F3",
        icon: GitBranch,
        description: "Procesando nombres y denominaciones...",
      },
      {
        key: "nociones",
        label: "Comparando Nociones",
        color: "#4CAF50",
        icon: Brain,
        description: "Evaluando nociones y conceptos...",
      },
      {
        key: "impactos",
        label: "Comparando Impactos",
        color: "#9C27B0",
        icon: Zap,
        description: "Analizando impactos y efectos...",
      },
    ],
    []
  );

  // Inicializar matriz
  useEffect(() => {
    const matrix = [];
    for (let i = 0; i < 96; i++) {
      // 12x8 = 96 celdas
      matrix.push({
        id: i,
        phase: "tipo", // Empezar con tipo
        isActive: false,
        delay: 0,
      });
    }
    setMatrixData(matrix);
  }, []);

  // Ciclo de análisis automático - SIMPLIFICADO
  useEffect(() => {
    if (!isProcessing) return;

    let phaseIndex = 0;

    const updateMatrix = () => {
      const currentPhase = phases[phaseIndex].key;
      console.log(
        "Updating matrix with phase:",
        currentPhase,
        "phaseIndex:",
        phaseIndex
      );
      setCurrentPhase(currentPhase);

      // Crear patrón simple que siempre funciona
      setMatrixData((prevMatrix) =>
        prevMatrix.map((cell, index) => {
          // Patrón diagonal simple
          const row = Math.floor(index / 12);
          const col = index % 12;
          const delay = (row + col) * 100;

          return {
            ...cell,
            phase: currentPhase,
            isActive: true,
            delay: delay,
          };
        })
      );

      // Cambiar a la siguiente fase
      phaseIndex = (phaseIndex + 1) % phases.length;
    };

    // Ejecutar cada 2 segundos
    const interval = setInterval(updateMatrix, 2000);
    updateMatrix(); // Ejecutar inmediatamente

    return () => clearInterval(interval);
  }, [isProcessing, phases]);

  if (!isProcessing) return null;

  const currentPhaseInfo = phases.find((p) => p.key === currentPhase);

  return (
    <Paper
      elevation={0}
      sx={{
        background:
          "linear-gradient(135deg, rgba(33, 150, 243, 0.1), rgba(76, 175, 80, 0.1))",
        borderRadius: "20px",
        padding: "40px",
        textAlign: "center",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(20px)",
        maxWidth: "600px",
        margin: "0 auto",
      }}
    >
      {/* Título principal */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <Zap
            size={32}
            style={{
              color: currentPhaseInfo?.color || "#fff",
              marginRight: "12px",
              animation: `${pulseAnimation} 1.5s infinite`,
            }}
          />
          <Typography
            variant="h4"
            sx={{
              color: "white",
              fontWeight: "bold",
              background: `linear-gradient(45deg, ${
                currentPhaseInfo?.color || "#fff"
              }, #fff)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Análisis en Curso
          </Typography>
        </Box>
        <Typography
          variant="h6"
          sx={{
            color: "rgba(255, 255, 255, 0.9)",
            transition: "all 0.3s ease",
            textShadow: `0 0 10px ${currentPhaseInfo?.color || "#fff"}40`,
          }}
        >
          {currentPhaseInfo?.description || "Procesando glosarios..."}
        </Typography>
      </Box>

      {/* Matriz de análisis */}
      <AnalysisMatrix>
        {matrixData.length > 0
          ? matrixData.map((cell) => (
              <MatrixCell
                key={cell.id}
                phase={cell.phase}
                delay={cell.delay}
                isActive={cell.isActive}
              />
            ))
          : // Fallback si matrixData está vacío
            Array.from({ length: 96 }, (_, index) => (
              <MatrixCell
                key={index}
                phase={currentPhase}
                delay={index * 50}
                isActive={true}
              />
            ))}
      </AnalysisMatrix>

      {/* Indicadores de fase */}
      <Box sx={{ mt: 4, space: 2 }}>
        {phases.map((phase, index) => {
          const IconComponent = phase.icon;
          const isActive = currentPhase === phase.key;

          return (
            <Box key={phase.key} sx={{ mb: 2 }}>
              <PhaseIndicator isActive={isActive} color={phase.color}>
                <IconComponent size={20} className="phase-icon" />
                <Typography variant="body1" className="phase-text">
                  {phase.label}
                </Typography>
                {isActive && (
                  <Box
                    sx={{ ml: "auto", display: "flex", alignItems: "center" }}
                  >
                    <Box
                      sx={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: phase.color,
                        animation: `${pulseAnimation} 1s infinite`,
                      }}
                    />
                  </Box>
                )}
              </PhaseIndicator>

              {/* Línea de flujo entre fases */}
              {index < phases.length - 1 && (
                <Box
                  sx={{
                    height: "12px",
                    display: "flex",
                    alignItems: "center",
                    px: 2,
                  }}
                >
                  <FlowLine
                    isActive={isActive}
                    color={phase.color}
                    sx={{ width: "100%" }}
                  />
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Información adicional */}
      <Box
        sx={{
          mt: 3,
          p: 2,
          backgroundColor: "rgba(255, 255, 255, 0.05)",
          borderRadius: "8px",
        }}
      >
        <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
          {processingStep || "Ejecutando algoritmos de análisis lingüístico..."}
        </Typography>
      </Box>
    </Paper>
  );
};

export default AnalyticalLoadingScreen;
